import React, { useEffect, useState } from "react";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import "./LiveChannelStudio.scss";
import {
  buildLiveMediaUrl,
  deleteLiveChannel,
  fetchLiveFeed,
  importFreeLiveChannels,
  saveLiveChannel,
} from "../services/liveApi";

const DEFAULT_FORM = {
  channel_number: "",
  name: "",
  description: "",
  logo_url: "",
  stream_url: "",
  provider_name: "",
  provider_url: "",
  country_code: "",
  language_code: "",
  category: "General",
  is_featured: false,
  is_public: true,
  statut: 1,
};

function LiveChannelStudio() {
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const data = await fetchLiveFeed();
      setChannels(data?.channels || []);
      setCategories(data?.categories || []);
    } catch (error) {
      console.error("Studio Direct:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submitChannel = async (event) => {
    event.preventDefault();

    if (!form.name || !form.stream_url) {
      alert("Le nom et l'URL HLS sont obligatoires.");
      return;
    }

    try {
      setSaving(true);
      await saveLiveChannel({
        ...form,
        channel_number: Number(form.channel_number || channels.length + 1),
        is_featured: form.is_featured ? 1 : 0,
        is_public: form.is_public ? 1 : 0,
        statut: Number(form.statut || 1),
      });
      setForm(DEFAULT_FORM);
      await loadChannels();
    } catch (error) {
      console.error(error);
      alert(error.message || "Impossible d'ajouter cette chaine.");
    } finally {
      setSaving(false);
    }
  };

  const importPreset = async () => {
    try {
      setImporting(true);
      await importFreeLiveChannels({ preset: "europe-africa", limit: 220, perPlaylistLimit: 20 });
      await loadChannels();
    } catch (error) {
      console.error(error);
      alert(error.message || "Import Europe/Afrique impossible.");
    } finally {
      setImporting(false);
    }
  };

  const importPlaylist = async () => {
    if (!playlistUrl.trim()) {
      alert("Colle une URL de playlist M3U.");
      return;
    }

    try {
      setImporting(true);
      await importFreeLiveChannels({ playlistUrl: playlistUrl.trim(), limit: 250 });
      setPlaylistUrl("");
      await loadChannels();
    } catch (error) {
      console.error(error);
      alert(error.message || "Import playlist impossible.");
    } finally {
      setImporting(false);
    }
  };

  const removeChannel = async (channel) => {
    try {
      await deleteLiveChannel(channel.id);
      await loadChannels();
    } catch (error) {
      console.error(error);
      alert(error.message || "Suppression impossible.");
    }
  };

  return (
    <div className="page-content">
      <div className="live-studio">
        <header className="live-studio__header">
          <div>
            <p>Studio Direct</p>
            <h2>Chaines internet en direct</h2>
          </div>
          <button className="live-studio__primary" onClick={importPreset} disabled={importing}>
            <SyncIcon /> {importing ? "Import..." : "Importer Europe/Afrique"}
          </button>
        </header>

        <section className="live-studio__grid">
          <form className="live-studio__panel live-studio__form" onSubmit={submitChannel}>
            <div className="live-studio__panelTitle">
              <LiveTvIcon />
              <h3>Ajouter une chaine</h3>
            </div>

            <label>
              Numero
              <input value={form.channel_number} onChange={(event) => updateField("channel_number", event.target.value)} placeholder="101" />
            </label>
            <label>
              Nom
              <input value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="France 24" />
            </label>
            <label className="live-studio__full">
              URL HLS / M3U8
              <input value={form.stream_url} onChange={(event) => updateField("stream_url", event.target.value)} placeholder="https://.../index.m3u8" />
            </label>
            <label>
              Logo
              <input value={form.logo_url} onChange={(event) => updateField("logo_url", event.target.value)} placeholder="https://...logo.png" />
            </label>
            <label>
              Categorie
              <input list="live-categories" value={form.category} onChange={(event) => updateField("category", event.target.value)} />
              <datalist id="live-categories">
                {categories.map((item) => (
                  <option key={item} value={item} />
                ))}
              </datalist>
            </label>
            <label>
              Pays
              <input value={form.country_code} onChange={(event) => updateField("country_code", event.target.value.toUpperCase())} placeholder="FR, CD, CG..." />
            </label>
            <label>
              Langue
              <input value={form.language_code} onChange={(event) => updateField("language_code", event.target.value.toLowerCase())} placeholder="fr, en, ln..." />
            </label>
            <label>
              Fournisseur
              <input value={form.provider_name} onChange={(event) => updateField("provider_name", event.target.value)} placeholder="IPTV-org, partenaire..." />
            </label>
            <label>
              URL fournisseur
              <input value={form.provider_url} onChange={(event) => updateField("provider_url", event.target.value)} placeholder="https://..." />
            </label>
            <label className="live-studio__full">
              Description
              <textarea rows="3" value={form.description} onChange={(event) => updateField("description", event.target.value)} />
            </label>
            <label className="live-studio__check">
              <input type="checkbox" checked={form.is_featured} onChange={(event) => updateField("is_featured", event.target.checked)} />
              Mettre en avant
            </label>
            <label className="live-studio__check">
              <input type="checkbox" checked={form.is_public} onChange={(event) => updateField("is_public", event.target.checked)} />
              Visible client
            </label>

            <button className="live-studio__primary live-studio__full" type="submit" disabled={saving}>
              {saving ? "Enregistrement..." : "Ajouter la chaine"}
            </button>
          </form>

          <section className="live-studio__panel">
            <div className="live-studio__panelTitle">
              <SyncIcon />
              <h3>Import en masse</h3>
            </div>
            <p className="live-studio__hint">
              Colle une playlist M3U/M3U8 de ton futur fournisseur prive, ou utilise l'import Europe/Afrique temporaire.
            </p>
            <label>
              URL playlist M3U
              <input value={playlistUrl} onChange={(event) => setPlaylistUrl(event.target.value)} placeholder="https://fournisseur.com/playlist.m3u" />
            </label>
            <button className="live-studio__secondary" onClick={importPlaylist} disabled={importing}>
              Importer cette playlist
            </button>

            <div className="live-studio__summary">
              <div>
                <strong>{channels.length}</strong>
                <span>chaines</span>
              </div>
              <div>
                <strong>{categories.length}</strong>
                <span>categories</span>
              </div>
            </div>
          </section>
        </section>

        <section className="live-studio__panel">
          <div className="live-studio__panelTitle">
            <LiveTvIcon />
            <h3>Chaines ajoutees</h3>
          </div>

          <div className="live-studio__list">
            {loading ? <div className="live-studio__empty">Chargement...</div> : null}
            {!loading &&
              channels.map((channel) => (
                <article key={channel.id} className="live-studio__channel">
                  <span className="live-studio__number">{String(channel.channelNumber || 0).padStart(2, "0")}</span>
                  <img src={buildLiveMediaUrl(channel.logoUrl)} alt={channel.name} />
                  <div>
                    <strong>{channel.name}</strong>
                    <p>{channel.category} · {channel.countryCode || "Intl"} · {channel.languageCode || "langue"}</p>
                  </div>
                  <button onClick={() => removeChannel(channel)} aria-label={`Supprimer ${channel.name}`}>
                    <DeleteOutlineIcon />
                  </button>
                </article>
              ))}
            {!loading && !channels.length ? <div className="live-studio__empty">Aucune chaine pour le moment.</div> : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default LiveChannelStudio;
